import type { FieldValue } from 'firebase/firestore';
import { z } from 'zod';

const MessageImageSchema = z.object({
  url: z.string(),
});

const MessageLinksSchema = z.object({
  link: z.string(),
  title: z.string(),
});

const MessageSocialMediaAccountSchema = z.object({
  username: z.string(),
  url: z.string(),
});

export const SocialMediaAccountsSchema = z.object({
  facebook: z.array(MessageSocialMediaAccountSchema).optional(),
  instagram: z.array(MessageSocialMediaAccountSchema).optional(),
  X: z.array(MessageSocialMediaAccountSchema).optional(),
  tiktok: z.array(MessageSocialMediaAccountSchema).optional(),
  youtube: z.array(MessageSocialMediaAccountSchema).optional(),
});

const MessageRelatedQuestionsSchema = z.object({
  title: z.string(),
});

export const DigitalInformationSchema = z.object({
  digitalPresenceScore: z.number().optional(),
  flags: z
    .object({
      greenFlag: z.string().optional(),
      redFlag: z.string().optional(),
    })
    .optional(),
  labels: z
    .object({
      careerCoded: z.string().optional(),
      suspiciouslyClean: z.string().optional(),
      invisibleOnline: z.string().optional(),
      selectiveSharer: z.string().optional(),
    })
    .optional(),
  relationshipForecast: z.string().optional(),
});

export const MessageUserImageSchema = z.object({
  mimeType: z.string(),
  url: z.string(),
});

const BaseMessageItemSchema = z.object({
  id: z.string(),
  role: z.enum(['assistant', 'user']),
  createdAt: z.union([z.number(), z.custom<FieldValue>()]).nullable(),
  updatedAt: z
    .union([z.number(), z.custom<FieldValue>()])
    .nullable()
    .optional(),
  images: z.array(MessageImageSchema).optional(),
  links: z.array(MessageLinksSchema).optional(),
  relatedQuestions: z.array(MessageRelatedQuestionsSchema).optional(),
  socialMediaAccounts: SocialMediaAccountsSchema.optional(),
  digitalInformation: DigitalInformationSchema.optional(),
});

const SuccessfulMessageItemSchema = BaseMessageItemSchema.extend({
  content: z.string().nullable(),
  error: z.never().optional(),
});

const MessageErrorSchema = z.object({
  status: z.union([z.string(), z.number()]),
  code: z.union([z.string(), z.number()]).optional(),
  type: z.string().optional(),
});

const ErrorMessageItemSchema = BaseMessageItemSchema.extend({
  content: z.never().optional(),
  error: MessageErrorSchema,
});

const MessageItemSchema = SuccessfulMessageItemSchema.or(
  ErrorMessageItemSchema,
);

export type MessageErrorType = z.infer<typeof MessageErrorSchema>;
export type MessageItemType = z.infer<typeof MessageItemSchema>;
export type MessageListType = MessageItemType[];
export type MessageImageType = z.infer<typeof MessageImageSchema>;
export type MessageLinksType = z.infer<typeof MessageLinksSchema>;
export type MessageSocialAccountsType = z.infer<
  typeof MessageSocialMediaAccountSchema
>;
export type SocialMediaAccountsType = z.infer<typeof SocialMediaAccountsSchema>;
export type MessageRelatedQuestionsType = z.infer<
  typeof MessageRelatedQuestionsSchema
>;
export type DigitalInformationType = z.infer<typeof DigitalInformationSchema>;

export type MessageUserImageType = z.infer<typeof MessageUserImageSchema>;

// API types
export interface ApiMessageItemType {
  role: MessageItemType['role'];
  content: NonNullable<MessageItemType['content']>;
  images?: MessageItemType['images'];
  links?: MessageItemType['links'];
  relatedQuestions?: MessageItemType['relatedQuestions'];
  socialMediaAccounts?: MessageItemType['socialMediaAccounts'];
  digitalInformation?: MessageItemType['digitalInformation'];
}

// Firebase data types
const DatabaseBaseMessageItemSchema = BaseMessageItemSchema.omit({
  id: true,
}).extend({
  createdAt: z.number(),
  // `updatedAt` is a mandatory field but since I can't run
  // migrations on Firebase Realtime Database, it needs to be
  // optional to parse existing data.
  updatedAt: z.number().optional(),
  images: z.array(MessageImageSchema).optional(),
  links: z.array(MessageLinksSchema).optional(),
  relatedQuestions: z.array(MessageRelatedQuestionsSchema).optional(),
  socialMediaAccounts: SocialMediaAccountsSchema.optional(),
  digitalInformation: DigitalInformationSchema.optional(),
});

const SuccessfulDatabaseMessageItemSchema =
  DatabaseBaseMessageItemSchema.extend({
    content: z.string(),
    error: z.never().optional(),
  });

const ErrorDatabaseMessageItemSchema = DatabaseBaseMessageItemSchema.extend({
  content: z.never().optional(),
  error: z.object({
    status: z.number(),
    code: z.number().optional(),
  }),
});

export const DatabaseMessageItemSchema = SuccessfulDatabaseMessageItemSchema.or(
  ErrorDatabaseMessageItemSchema,
);

export type DatabaseDraftMessageItemType = z.infer<
  typeof DatabaseMessageItemSchema
>;
